import {
  PITCH_PROOF_MAX_ITEMS,
  type PitchProofChoice,
  type PitchProofItem,
} from 'fym-shared/src/music24/outreach/pitchProof';

/** The choice that `campaigns.update` takes, from the stored items. */
export function choicesOf(items: PitchProofItem[]): PitchProofChoice[] {
  return items.map((item) => (item.key === 'custom' ? { key: 'custom', text: item.value } : { key: item.key }));
}

/** Turns one data key on or off. A key on a full choice stays off. */
export function toggleProofKey(items: PitchProofItem[], option: PitchProofItem): PitchProofChoice[] | null {
  const on = items.some((item) => item.key === option.key);
  if (on) {
    return choicesOf(items.filter((item) => item.key !== option.key));
  }
  if (items.length >= PITCH_PROOF_MAX_ITEMS) {
    return null;
  }
  return [...choicesOf(items), { key: option.key }];
}

/** Puts the free line in (or takes it out when empty). Null when the choice is full. */
export function withFreeLine(items: PitchProofItem[], text: string): PitchProofChoice[] | null {
  const rest = choicesOf(items.filter((item) => item.key !== 'custom'));
  if (text.trim() === '') {
    return rest;
  }
  if (rest.length >= PITCH_PROOF_MAX_ITEMS) {
    return null;
  }
  return [...rest, { key: 'custom', text: text.trim() }];
}

/** One row of the proof picker: an option of our data, or a stored item that is not an option now. */
export interface ProofRow {
  item: PitchProofItem;
  /** True for a stored item whose number is not in our data now (it can only be unchecked). */
  stale: boolean;
}

/**
 * The rows of the picker: every option of our data, then each chosen data item that is not an
 * option now. The server keeps such an item while it stays chosen, so the artist must see it and
 * can uncheck it.
 */
export function proofRows(options: PitchProofItem[], chosen: PitchProofItem[]): ProofRow[] {
  const stale = chosen.filter((item) => item.key !== 'custom' && !options.some((option) => option.key === item.key));
  return [...options.map((item) => ({ item, stale: false })), ...stale.map((item) => ({ item, stale: true }))];
}
